import DateFnsUtils from '@date-io/date-fns';
import { Grid, InputAdornment, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { UsuarioCadastrosRequest } from 'api/Assessor/usuario';
import { Calendar, DropdownIcon, SyncIcon } from 'components/Icons';
import ptBR from 'date-fns/locale/pt-BR';
import ErrorHandler from 'helpers/utils/ErrorHandler';
import translation from 'locales/translation';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { assessorActionListarCooperativasUsuario } from 'store/modules/assessor/actions';
import { IReducer } from 'store/Reducer';
import { filtrosInitialState, filtrosReducer } from './reducer';
import useStyle from './style';

interface Props {
  onChangeFilter: (
    filters: Partial<UsuarioCadastrosRequest> | undefined
  ) => void;
  onValidateDate: (hasError: boolean) => void;
}

let searchDebounce: number;

export default function ListagemCadastrosFiltros({
  onChangeFilter,
  onValidateDate,
}: Props) {
  const classes = useStyle();
  const dispatch = useDispatch();
  const { cooperativas, cooperativasIsLoading } = useSelector(
    (state: IReducer) => state.assessor
  );
  const [{ coop, startDate, endDate, search }, action] = useReducer(
    filtrosReducer,
    filtrosInitialState
  );

  const maxStartDate = useMemo(() => {
    return new Date(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
      0,
      0,
      0
    );
  }, [endDate]);

  const hasDateError = useMemo(() => {
    if (endDate < startDate) return true;
    if (startDate > maxStartDate) return true;
    return false;
  }, [maxStartDate, startDate, endDate]);

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.currentTarget.value = e.currentTarget.value
        .replace(/[-.]/g, '')
        .toUpperCase();
      const value = e.currentTarget.value;
      window.clearTimeout(searchDebounce);
      if (value.length < 4) {
        search.length >= 4 && action({ type: 'setSearch', value: '' });
        return;
      }
      searchDebounce = window.setTimeout(() => {
        action({ type: 'setSearch', value });
      }, 1000);
    },
    [search]
  );

  const filters: Partial<UsuarioCadastrosRequest> | undefined = useMemo(() => {
    if (!coop?.length || hasDateError) return;
    return {
      cpfOrNome: search,
      coop,
      dataInicioCadastro: moment(startDate).format('YYYY-MM-DDTHH:mm:ss'),
      dataFimCadastro: moment(endDate).format('YYYY-MM-DDTHH:mm:ss'),
    } as Partial<UsuarioCadastrosRequest>;
  }, [coop, hasDateError, startDate, endDate, search]);

  useEffect(() => {
    if (cooperativas !== null || cooperativasIsLoading) return;
    dispatch(assessorActionListarCooperativasUsuario(undefined, ErrorHandler));
  }, [dispatch, cooperativas, cooperativasIsLoading]);

  useEffect(() => {
    onChangeFilter(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    onValidateDate(hasDateError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDateError]);

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ptBR}>
      <Grid container spacing={4}>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Autocomplete
            options={cooperativas || []}
            getOptionLabel={(option) =>
              `${option.codCooperativa} - ${option.nomeCooperativa}`
            }
            color='primary'
            disabled={cooperativasIsLoading}
            onChange={(e, newValue) =>
              action({ type: 'setCoop', value: newValue?.codCooperativa })
            }
            noOptionsText={translation.ListaLogins.filtroCoop.noOptionsText}
            popupIcon={
              cooperativasIsLoading ? (
                <SyncIcon className={classes.selectIcon} animated />
              ) : (
                <DropdownIcon className={classes.selectIcon} />
              )
            }
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                label={translation.ListaLogins.filtroCoop.label}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <DatePicker
            label={translation.ListaLogins.filtroStartDate.label}
            value={startDate}
            onChange={(newDate) =>
              action({
                type: 'setStartDate',
                value: newDate
                  ? new Date(
                      newDate.getFullYear(),
                      newDate.getMonth(),
                      newDate.getDate(),
                      0,
                      0,
                      0
                    )
                  : null,
              })
            }
            maxDate={maxStartDate}
            format="'De' dd/MM/yyyy"
            color='primary'
            cancelLabel={translation.ListaLogins.filtroStartDate.cancelLabel}
            maxDateMessage={
              translation.ListaLogins.filtroStartDate.maxDateError
            }
            minDateMessage={
              translation.ListaLogins.filtroStartDate.minDateError
            }
            inputProps={{
              className: classes.dateField,
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment
                  position='end'
                  className={classes.dateFieldAdornment}
                >
                  <Calendar />
                </InputAdornment>
              ),
            }}
            variant='inline'
            disabled={cooperativasIsLoading || !coop?.length}
            disableToolbar
            required
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <DatePicker
            label={translation.ListaLogins.filtroEndDate.label}
            value={endDate}
            onChange={(newDate) =>
              action({
                type: 'setEndDate',
                value: newDate
                  ? new Date(
                      newDate.getFullYear(),
                      newDate.getMonth(),
                      newDate.getDate(),
                      23,
                      59,
                      59
                    )
                  : null,
              })
            }
            maxDate={new Date()}
            format="'Até' dd/MM/yyyy"
            color='primary'
            cancelLabel={translation.ListaLogins.filtroEndDate.cancelLabel}
            inputProps={{
              className: classes.dateField,
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment
                  position='end'
                  className={classes.dateFieldAdornment}
                >
                  <Calendar />
                </InputAdornment>
              ),
            }}
            variant='inline'
            disabled={cooperativasIsLoading || !coop?.length}
            disableToolbar
            required
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6} md={12} lg={5}>
          <TextField
            label={translation.ListaLogins.filtroSearch.label}
            onChange={handleSearch}
            disabled={cooperativasIsLoading || !coop?.length}
            fullWidth
          />
        </Grid>
      </Grid>
    </MuiPickersUtilsProvider>
  );
}
